import React, { useState, useMemo, useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import QuestionImageDisplay from './questions/QuestionImageDisplay';
import MathRenderer from './MathRenderer';
import ProbabilisticResponseFeedback from './questions/ProbabilisticResponseFeedback';
import ProbabilisticCriteriaList from './questions/ProbabilisticCriteriaList';
import { gradeProbabilisticResponse, isProbabilistic } from '../utils/probabilisticGrading';
import { scoreQuiz, formatMarks, isAnswered } from '../utils/quizScore';
import { normalizeMath, evalNumber } from '../utils/answerMatching';

const getQuestionGroupKey = (q) => {
  const parent = q?.parent_question_number;
  if (!parent) return `single-${q?.id}`;

  const scope =
    q?.doc_external_id ??
    q?.document_external_id ??
    q?.subject_module_id ??
    q?.subject_id ??
    q?.year ??
    'default';

  return `${scope}::${parent}`;
};

// Three outcomes, not two: an answer that earned some of its marks is neither a pass
// nor a miss, and colouring it red tells a student who was half-right that they were
// wholly wrong.
const STATUS_TONE = {
  correct: { badge: 'bg-emerald-500/20 text-emerald-400', Icon: CheckIcon },
  partial: { badge: 'bg-amber-500/20 text-amber-300', Icon: RemoveIcon },
  incorrect: { badge: 'bg-rose-500/20 text-rose-400', Icon: CloseIcon },
};

// The trigger and the explanation itself occupy the same slot, so the explanation
// opens exactly where the student was already looking instead of surfacing somewhere
// else on the card. Dismissing it puts the trigger back.
const ExplanationDisclosure = ({ open, triggerLabel, title, onToggle, children }) => {
  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white/85 hover:bg-white/20 hover:text-white transition"
      >
        <span>{triggerLabel}</span>
        <span aria-hidden="true">▾</span>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50">{title}</p>
        <button
          type="button"
          onClick={onToggle}
          aria-label={`Hide ${title.toLowerCase()}`}
          className="-mr-1.5 -mt-1.5 shrink-0 rounded-full p-1 text-white/45 hover:bg-white/10 hover:text-white transition"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>

      <div className="mt-2 text-sm sm:text-base break-words overflow-x-auto">{children}</div>
    </div>
  );
};

const QuizComplete = ({ questionsFromDatabase, userAnswers }) => {
  const [explanationsVisibility, setExplanationsVisibility] = useState({});
  const [imagesVisibility, setImagesVisibility] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  // ── Group questions by parent_question_number ──────────────────────────────
  const groups = useMemo(() => {
    const result = [];
    const parentMap = {};

    (questionsFromDatabase ?? []).forEach((q) => {
      const parent = q.parent_question_number;
      const groupKey = getQuestionGroupKey(q);

      if (parent && groupKey) {
        if (parentMap[groupKey] !== undefined) {
          result[parentMap[groupKey]].questions.push(q);
        } else {
          parentMap[groupKey] = result.length;
          result.push({ key: groupKey, parent, questions: [q], isMultiPart: true });
        }
      } else {
        result.push({
          key: `single-${q.id}`,
          parent: null,
          questions: [q],
          isMultiPart: false,
        });
      }
    });

    result.forEach((g) => {
      if (g.isMultiPart) {
        g.questions.sort((a, b) =>
          (a.question_number ?? '').localeCompare(
            b.question_number ?? '',
            undefined,
            { numeric: true }
          )
        );
      }
    });

    return result;
  }, [questionsFromDatabase]);

  const subLabelFor = (q, parent) => {
    const sub = (q.question_number ?? '').replace(new RegExp(`^${parent}`), '');
    return sub.split('_').map((p) => `(${p})`).join('');
  };

  const splitPreamble = (content) => {
    const parts = (content ?? '').split(/\n\n+/);
    if (parts.length > 1) {
      return {
        preamble: parts.slice(0, -1).join('\n\n'),
        equation: parts[parts.length - 1],
      };
    }
    return { preamble: null, equation: content ?? '' };
  };

  const handleExplanation = (questionId) => {
    setExplanationsVisibility((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleImageToggle = (questionId) => {
    setImagesVisibility((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const checkAnswer = (question, userAnswer) => {
    const questionType = question?.question_type;
    const answers = question?.answers ?? [];
    if (!answers.length) return false;

    if (questionType === 'fill_in_the_blank') {
      const userArr = Array.isArray(userAnswer) ? userAnswer : [];
      return answers.every((answerObj, i) => {
        const userVal = normalizeMath(userArr[i] ?? '');
        const correctVal = normalizeMath(answerObj?.content ?? '');
        const tol = parseFloat(answerObj?.tolerance ?? 0);

        const userNum = evalNumber(userVal);
        const correctNum = evalNumber(correctVal);

        if (!isNaN(userNum) && !isNaN(correctNum)) {
          return Math.abs(userNum - correctNum) <= (isNaN(tol) ? 0 : tol);
        }

        return answerObj?.case_sensitive
          ? String(userArr[i] ?? '').trim() === String(answerObj?.content ?? '').trim()
          : userVal === correctVal;
      });
    }

    return answers.some((answerObj) => {
      const correctAnswer = answerObj?.content;

      if (questionType === 'numeric_input') {
        const userVal    = normalizeMath(userAnswer);
        const correctVal = normalizeMath(correctAnswer);
        const userNum    = evalNumber(userVal);
        const correctNum = evalNumber(correctVal);
        const tolerance  = parseFloat(answerObj?.tolerance ?? 0);

        // Path 1 — both sides parse as plain numbers: numeric compare with tolerance.
        // Handles fractions (1/4 == 0.25 == 2/8), decimals, integers.
        if (!isNaN(userNum) && !isNaN(correctNum)) {
          return Math.abs(userNum - correctNum) <= (isNaN(tolerance) ? 0 : tolerance);
        }

        // Path 2 — non-numeric structured answer (coordinate pair, ratio, list,
        // algebraic expression). Fall back to normalised string match.
        return userVal === correctVal;
      }

      if (questionType === 'text_input') {
        return answerObj?.case_sensitive
          ? String(userAnswer ?? '').trim() === String(correctAnswer ?? '').trim()
          : String(userAnswer ?? '').trim().toLowerCase() === String(correctAnswer ?? '').trim().toLowerCase();
      }

      return String(userAnswer ?? '').trim() === String(correctAnswer ?? '').trim();
    });
  };

  const isBlankCorrect = (answerObj, userVal) => {
    const userNorm = normalizeMath(userVal ?? '');
    const correctNorm = normalizeMath(answerObj?.content ?? '');
    const tol = parseFloat(answerObj?.tolerance ?? 0);

    const userNum = evalNumber(userNorm);
    const correctNum = evalNumber(correctNorm);

    if (!isNaN(userNum) && !isNaN(correctNum)) {
      return Math.abs(userNum - correctNum) <= (isNaN(tol) ? 0 : tol);
    }

    return answerObj?.case_sensitive
      ? String(userVal ?? '').trim() === String(answerObj?.content ?? '').trim()
      : userNorm === correctNorm;
  };

  // Open-ended answers are marked by AI. This is practice, so nothing is recorded
  // against the student's progress — `practice: true` grades without persisting.
  const [probabilisticResultById, setProbabilisticResultById] = useState({});

  // Blank answers are never sent for marking: they score nothing either way, so the
  // request would only spend money to be told the student wrote nothing. Anything that
  // waits on marking has to wait on THIS list — waiting on every open-ended question
  // would wait forever for a verdict that is never coming.
  const questionsAwaitingMark = useMemo(
    () =>
      (questionsFromDatabase ?? []).filter(
        (q) => isProbabilistic(q) && isAnswered(userAnswers?.[q.id])
      ),
    [questionsFromDatabase, userAnswers]
  );

  useEffect(() => {
    const openQuestions = questionsAwaitingMark;
    if (openQuestions.length === 0) return;

    let cancelled = false;
    setProbabilisticResultById((prev) => {
      const next = { ...prev };
      openQuestions.forEach((q) => {
        if (!next[q.id]) next[q.id] = { state: 'grading' };
      });
      return next;
    });

    (async () => {
      for (const q of openQuestions) {
        const verdict = await gradeProbabilisticResponse({
          questionId: q.id,
          studentAnswer: userAnswers?.[q.id] ?? '',
          practice: true,
        });
        if (cancelled) return;

        setProbabilisticResultById((prev) => ({
          ...prev,
          [q.id]: verdict.ok
            ? {
                state: 'graded',
                outcome: verdict.outcome,
                marksAwarded: verdict.marksAwarded,
                maxMarks: verdict.maxMarks,
                criteria: verdict.criteria,
                feedback: verdict.feedback,
              }
            : { state: 'failed', message: verdict.message, retryable: verdict.retryable },
        }));
      }
    })();

    return () => { cancelled = true; };
  }, [questionsAwaitingMark, userAnswers]);

  const questionCorrectById = useMemo(() => {
    const entries = (questionsFromDatabase ?? []).map((q) => [
      q.id,
      isProbabilistic(q)
        ? probabilisticResultById[q.id]?.outcome === 'correct'
        : checkAnswer(q, userAnswers?.[q.id]),
    ]);
    return Object.fromEntries(entries);
  }, [questionsFromDatabase, userAnswers, probabilisticResultById]);

  // An open-ended answer can land between right and wrong; a multi-part question lands
  // there whenever some parts were right. Both read as "partial".
  const questionPartialById = useMemo(() => {
    const entries = (questionsFromDatabase ?? []).map((q) => [
      q.id,
      isProbabilistic(q) && probabilisticResultById[q.id]?.outcome === 'partial',
    ]);
    return Object.fromEntries(entries);
  }, [questionsFromDatabase, probabilisticResultById]);

  const groupMeta = useMemo(() => {
    return groups.map((group, gIdx) => {
      const correctCount = group.questions.filter((q) => questionCorrectById[q.id]).length;
      const partialCount = group.questions.filter((q) => questionPartialById[q.id]).length;
      const total = group.questions.length;
      const allCorrect = correctCount === total;
      const status = allCorrect
        ? 'correct'
        : correctCount > 0 || partialCount > 0
        ? 'partial'
        : 'incorrect';

      return {
        ...group,
        displayIndex: gIdx + 1,
        correctCount,
        total,
        allCorrect,
        status,
        needsReview: !allCorrect,
      };
    });
  }, [groups, questionCorrectById, questionPartialById]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;

      groupMeta.forEach((group, idx) => {
        if (next[group.key] === undefined) {
          next[group.key] = group.needsReview || idx === 0;
          changed = true;
        }
      });

      Object.keys(next).forEach((key) => {
        if (!groupMeta.some((group) => group.key === key)) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [groupMeta]);

  const filteredGroups = useMemo(() => {
    if (activeFilter === 'incorrect') {
      return groupMeta.filter((group) => group.needsReview);
    }
    if (activeFilter === 'correct') {
      return groupMeta.filter((group) => group.allCorrect);
    }
    return groupMeta;
  }, [groupMeta, activeFilter]);

  const totalQuestions = questionsFromDatabase?.length ?? 0;
  const correctCount = (questionsFromDatabase ?? []).filter(
    (q) => questionCorrectById[q.id]
  ).length;
  const incorrectCount = totalQuestions - correctCount;

  const score = useMemo(
    () =>
      scoreQuiz(
        (questionsFromDatabase ?? []).map((q) => ({
          question: q,
          answered: isAnswered(userAnswers?.[q.id]),
          correct: questionCorrectById[q.id],
          verdict: probabilisticResultById[q.id],
        }))
      ),
    [questionsFromDatabase, userAnswers, questionCorrectById, probabilisticResultById]
  );

  const gradeSettled = score.pending === 0;
  const grade = score.percent.toFixed(1);

  const filterCounts = {
    all: groupMeta.length,
    incorrect: groupMeta.filter((g) => g.needsReview).length,
    correct: groupMeta.filter((g) => g.allCorrect).length,
  };

  const expandAllVisible = () => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        next[group.key] = true;
      });
      return next;
    });
  };

  const collapseAllVisible = () => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        next[group.key] = false;
      });
      return next;
    });
  };

  // If content has bare LaTeX commands (\frac, \sqrt, etc.) but no $ delimiters,
  // wrap it so MathRenderer can render it correctly.
  const wrapBareLatex = (content) => {
    if (!content) return content;
    const hasLatexCommands = /\\[a-zA-Z]/.test(String(content));
    const hasDelimiters = /\$/.test(String(content));
    if (hasLatexCommands && !hasDelimiters) return `$${content}$`;
    return content;
  };

  const renderAnswerValue = (value, className = '') => {
    const isEmpty =
      value == null || (typeof value === 'string' && value.trim() === '');

    if (isEmpty) {
      return <span className={`text-white/40 ${className}`}>No answer</span>;
    }

    return (
      <span className={className}>
        <MathRenderer content={wrapBareLatex(String(value))} />
      </span>
    );
  };

  // Open-ended answers are marked by AI after the quiz is submitted. Showing the
  // results while that is still running would report an unmarked answer as wrong,
  // so hold the summary until every answer actually has a mark.
  const pendingMarking = questionsAwaitingMark.filter(
    (q) => probabilisticResultById[q.id]?.state !== 'graded'
        && probabilisticResultById[q.id]?.state !== 'failed'
  );

  if (pendingMarking.length > 0) {
    const total = questionsAwaitingMark.length;
    const done = total - pendingMarking.length;

    return (
      <div className="w-full min-h-[100dvh] bg-[#133042] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-sky-400" />
            </span>
          </div>

          <h2 className="text-xl font-semibold">Grading your answers</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            Hold tight — this takes a few seconds. Your results will appear as soon as
            everything has been marked.
          </p>

          {total > 1 && (
            <p className="mt-5 text-xs font-medium tabular-nums text-white/40">
              {done} of {total} marked
            </p>
          )}

          <div className="mx-auto mt-4 h-1 w-56 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-sky-400 transition-all duration-500 ease-out"
              style={{ width: `${total > 0 ? Math.round((done / total) * 100) : 0}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[100dvh] bg-[#133042] text-white overscroll-y-none">
      <div className="flex flex-col min-h-[100dvh]">
        <div className="flex w-full">
          <div className="w-1/2">
            <div className="font-thin px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base relative bg-[#1c394a] text-white hover:cursor-pointer">
              <div className="text-left">
                <div
                  onClick={() => {
                    const segments = window.location.pathname.split('/').filter(Boolean);
                    window.location.href = '/' + segments.slice(0, 2).join('/');
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out"
                />
                Subject Overview
              </div>
            </div>
          </div>
          <div className="w-1/2">
            <div
              onClick={() => window.location.reload()}
              className="font-thin px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base relative bg-[#1c394a] text-white hover:cursor-pointer"
            >
              <div className="text-right">
                <div className="absolute inset-0 bg-gradient-to-r from-[#133042] via-[#2e92b0] to-[#133042] opacity-0 hover:opacity-40 transition-opacity duration-300 ease-in-out" />
                Restart Quiz
              </div>
            </div>
          </div>
        </div>

        <div className="w-full py-6 sm:py-8 px-4 sm:px-10 bg-white text-slate-900">
          <div className="flex flex-col gap-5">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight text-left break-words">
              Quiz Completed
            </h2>

            <>
              {/* Mobile pills */}
              <div className="flex flex-wrap gap-2 sm:hidden">
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                  Total <span className="ml-1 font-semibold text-slate-900">{totalQuestions}</span>
                </div>
                <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                  Correct <span className="ml-1 font-semibold text-emerald-900">{correctCount}</span>
                </div>
                <div className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700">
                  Review <span className="ml-1 font-semibold text-amber-900">{incorrectCount}</span>
                </div>
                <div className="rounded-full bg-slate-900 px-3 py-2 text-sm font-medium text-slate-300">
                  Grade{' '}
                  {gradeSettled ? (
                    <span className="ml-1 font-semibold text-white">
                      {grade}%{' '}
                      <span className="font-normal text-slate-400">
                        ({formatMarks(score.earned)}/{formatMarks(score.possible)})
                      </span>
                    </span>
                  ) : (
                    <span className="ml-1 font-semibold text-slate-500">—</span>
                  )}
                </div>
              </div>

              {/* Desktop/tablet cards */}
              <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total questions</p>
                  <p className="mt-2 text-2xl font-semibold">{totalQuestions}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-wide text-emerald-700">Correct</p>
                  <p className="mt-2 text-2xl font-semibold text-emerald-800">{correctCount}</p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-wide text-amber-700">Needs review</p>
                  <p className="mt-2 text-2xl font-semibold text-amber-800">{incorrectCount}</p>
                </div>
                <div className="rounded-2xl bg-slate-900 px-4 py-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Grade</p>
                  {gradeSettled ? (
                    <>
                      <p className="mt-2 text-2xl font-semibold text-white">{grade}%</p>
                      <p className="mt-1 text-xs text-slate-400">
                        {formatMarks(score.earned)} of {formatMarks(score.possible)} marks
                        {score.skipped > 0 && ` · ${score.skipped} skipped`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mt-2 text-2xl font-semibold text-slate-500">—</p>
                      <p className="mt-1 text-xs text-slate-400">Still marking</p>
                    </>
                  )}
                </div>
              </div>
            </>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
  {/* Mobile filter dropdown */}
  <div className="sm:hidden">
    <label className="block text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">
      Review filter
    </label>
    <select
      value={activeFilter}
      onChange={(e) => setActiveFilter(e.target.value)}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-slate-400"
    >
      <option value="all">All ({filterCounts.all})</option>
      <option value="incorrect">Needs review ({filterCounts.incorrect})</option>
      <option value="correct">Correct ({filterCounts.correct})</option>
    </select>
  </div>

  {/* Desktop/tablet filter pills */}
  <div className="hidden sm:flex flex-wrap gap-2">
    <button
      type="button"
      onClick={() => setActiveFilter('all')}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
        activeFilter === 'all'
          ? 'bg-slate-900 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      All ({filterCounts.all})
    </button>
    <button
      type="button"
      onClick={() => setActiveFilter('incorrect')}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
        activeFilter === 'incorrect'
          ? 'bg-amber-600 text-white'
          : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
      }`}
    >
      Needs review ({filterCounts.incorrect})
    </button>
    <button
      type="button"
      onClick={() => setActiveFilter('correct')}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${
        activeFilter === 'correct'
          ? 'bg-emerald-600 text-white'
          : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
      }`}
    >
      Correct ({filterCounts.correct})
    </button>
  </div>

  <div className="flex flex-wrap gap-2">
    <button
      type="button"
      onClick={expandAllVisible}
      className="rounded-full bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200 transition"
    >
      Expand all
    </button>
    <button
      type="button"
      onClick={collapseAllVisible}
      className="rounded-full bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200 transition"
    >
      Collapse all
    </button>
  </div>
</div>
          </div>
        </div>

        <div className="min-h-screen h-full px-4 sm:px-10 py-5 flex flex-col font-thin text-white">
          {filteredGroups.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/10 px-5 py-6 text-white/80">
              No questions in this view.
            </div>
          ) : (
            filteredGroups.map((group) => {
              const expanded = !!expandedGroups[group.key];
              const tone = STATUS_TONE[group.status] ?? STATUS_TONE.incorrect;

              return (
                <div
                  key={group.key}
                  className="my-3 rounded-2xl border border-white/10 bg-white/10 backdrop-blur-sm overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full px-4 sm:px-6 py-4 text-left hover:bg-white/5 transition"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div
                        className={`flex gap-3 min-w-0 ${
                          group.isMultiPart ? 'items-start' : 'items-center'
                        }`}
                      >
                        <div
                          className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                            group.isMultiPart ? 'mt-0.5' : ''
                          } ${tone.badge}`}
                        >
                          <tone.Icon fontSize="small" />
                        </div>

                        <div className="min-w-0">
                          <p className="text-base sm:text-lg font-medium text-white">
                            Question {group.displayIndex}
                          </p>
                          {group.isMultiPart && (
                            <p className="mt-0.5 text-sm text-white/60">
                              Multi-part · {group.correctCount} of {group.total} correct
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="shrink-0 flex items-center gap-2 text-sm text-white/70 self-start sm:self-auto">
                        <span>{expanded ? 'Hide' : 'Review'}</span>
                        <span className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>▾</span>
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-white/10 px-4 sm:px-6 py-5">
                      {group.isMultiPart ? (
                        (() => {
                          const firstSub = group.questions[0];
                          const { preamble } = splitPreamble(firstSub?.content);

                          return (
                            <div className="space-y-5">
                              <div className="divide-y divide-white/10">
                                {group.questions.map((question) => {
                                  const questionId = question.id;
                                  const isFirstSub = question.id === firstSub.id;
                                  const userAnswer = userAnswers?.[questionId];
                                  const isCorrect = questionCorrectById[questionId];
                                  const isPartial = questionPartialById[questionId];
                                  const isFillInTheBlank = question?.question_type === 'fill_in_the_blank';
                                  const correctAnswer = question?.answers?.[0]?.content;
                                  const explanation = question?.answers?.[0]?.explanation;
                                  const subLabel = subLabelFor(question, group.parent);
                                  const { preamble: subPreamble, equation } = splitPreamble(question?.content);
                                  // Only show inline sub-preamble if it differs from the shared top preamble
                                  const showSubPreamble = subPreamble && !isFirstSub && subPreamble.trim() !== preamble?.trim();

                                  return (
                                    <div
                                      key={`question-${questionId}`}
                                      className="py-5 space-y-4 first:pt-0 last:pb-0"
                                    >
                                      <div className="space-y-1 text-left">
                                        {/* Preamble sits above the whole row — vertically above the icon */}
                                        {isFirstSub && preamble && (
                                          <div className="mb-2 break-words text-white/90">
                                            <MathRenderer content={preamble} />
                                          </div>
                                        )}
                                        {showSubPreamble && (
                                          <div className="mb-2 break-words text-white/90">
                                            <MathRenderer content={subPreamble} />
                                          </div>
                                        )}

                                        {/* Icon always aligned with (a)/(b)/(c) label */}
                                        <div className="flex items-start gap-3">
                                          <div
                                            className={`mt-0.5 h-6 w-6 flex items-center justify-center shrink-0 ${
                                              isCorrect
                                                ? 'text-emerald-400'
                                                : isPartial
                                                ? 'text-amber-300'
                                                : 'text-rose-400'
                                            }`}
                                          >
                                            {isCorrect ? (
                                              <CheckIcon fontSize="small" />
                                            ) : isPartial ? (
                                              <RemoveIcon fontSize="small" />
                                            ) : (
                                              <CloseIcon fontSize="small" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0 break-words">
                                            <span className="font-semibold text-white/60 mr-2">{subLabel}</span>
                                            <MathRenderer content={equation} />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start">
                                        <div className="min-w-0 space-y-2">
                                          {isFillInTheBlank ? (
                                            (question?.answers ?? []).map((answerObj, i) => {
                                              const userVal = Array.isArray(userAnswer) ? userAnswer[i] : undefined;
                                              const blankCorrect = isBlankCorrect(answerObj, userVal);

                                              return (
                                                <div key={i} className="space-y-1">
                                                  <p className="font-semibold break-words leading-snug text-left">
                                                    <span className="text-white/75">Blank {i + 1} — Your Answer:</span>{' '}
                                                    {renderAnswerValue(
                                                      userVal,
                                                      blankCorrect ? 'text-emerald-400' : 'text-rose-400'
                                                    )}
                                                  </p>
                                                  {!blankCorrect && (
                                                    <p className="font-semibold break-words leading-snug text-left">
                                                      <span className="text-white/75">Blank {i + 1} — Correct Answer:</span>{' '}
                                                      <MathRenderer content={wrapBareLatex(answerObj?.content)} />
                                                    </p>
                                                  )}
                                                </div>
                                              );
                                            })
                                          ) : (
                                            <>
                                              <p className="font-semibold break-words leading-snug text-left">
                                                <span className="text-white/75">Your Answer:</span>{' '}
                                                {renderAnswerValue(
                                                  userAnswer,
                                                  isCorrect
                                                    ? 'text-emerald-400'
                                                    : isPartial
                                                    ? 'text-amber-300'
                                                    : 'text-rose-400'
                                                )}
                                              </p>

                                              {!isCorrect && (
                                                <p className="font-semibold break-words leading-snug text-left">
                                                  <span className="text-white/75">Correct Answer:</span>{' '}
                                                  <MathRenderer content={wrapBareLatex(correctAnswer)} />
                                                </p>
                                              )}
                                            </>
                                          )}
                                        </div>

                                        {question?.has_image && (
                                          <div className="flex flex-wrap gap-2 sm:justify-end sm:self-start sm:justify-self-end">
                                            <button
                                              type="button"
                                              onClick={() => handleImageToggle(questionId)}
                                              className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-sm hover:bg-white/20 transition"
                                            >
                                              <span>{imagesVisibility[questionId] ? 'Hide image' : 'Show image'}</span>
                                              <span className={`transition-transform ${imagesVisibility[questionId] ? 'rotate-180' : ''}`}>▾</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>

                                      {explanation && (
                                        <ExplanationDisclosure
                                          open={!!explanationsVisibility[questionId]}
                                          triggerLabel={
                                            isProbabilistic(question) ? 'Extra explanation' : 'View explanation'
                                          }
                                          title={isProbabilistic(question) ? 'Extra explanation' : 'Explanation'}
                                          onToggle={() => handleExplanation(questionId)}
                                        >
                                          <MathRenderer content={explanation} />
                                          <ProbabilisticCriteriaList
                                            criteria={probabilisticResultById[questionId]?.criteria}
                                            theme="dark"
                                          />
                                        </ExplanationDisclosure>
                                      )}

                                      {question?.has_image && imagesVisibility[questionId] && (
                                        <div className="mt-3">
                                          <QuestionImageDisplay questionId={question.id} />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        (() => {
                          const question = group.questions[0];
                          const questionId = question.id;
                          const isFillInTheBlank = question?.question_type === 'fill_in_the_blank';
                          const correctAnswer = question?.answers?.[0]?.content;
                          const explanation = question?.answers?.[0]?.explanation;
                          const userAnswer = userAnswers?.[questionId];
                          const isCorrect = questionCorrectById[questionId];
                          const isPartial = questionPartialById[questionId];

                          return (
                            <div className="space-y-4 text-left">
                              <div className="break-words">
                                <MathRenderer content={question?.content} />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start">
                                <div className="min-w-0 space-y-2">
                                  {isFillInTheBlank ? (
                                    (question?.answers ?? []).map((answerObj, i) => {
                                      const userVal = Array.isArray(userAnswer) ? userAnswer[i] : undefined;
                                      const blankCorrect = isBlankCorrect(answerObj, userVal);

                                      return (
                                        <div key={i} className="space-y-1">
                                          <p className="font-semibold break-words leading-snug text-left">
                                            <span className="text-white/75">Blank {i + 1} — Your Answer:</span>{' '}
                                            {renderAnswerValue(
                                              userVal,
                                              blankCorrect ? 'text-emerald-400' : 'text-rose-400'
                                            )}
                                          </p>
                                          {!blankCorrect && (
                                            <p className="font-semibold break-words leading-snug text-left">
                                              <span className="text-white/75">Blank {i + 1} — Correct Answer:</span>{' '}
                                              <MathRenderer content={wrapBareLatex(answerObj?.content)} />
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <>
                                      <p className="font-semibold break-words leading-snug text-left">
                                        <span className="text-white/75">Your Answer:</span>{' '}
                                        {renderAnswerValue(
                                          userAnswer,
                                          isCorrect
                                            ? 'text-emerald-400'
                                            : isPartial
                                            ? 'text-amber-300'
                                            : 'text-rose-400'
                                        )}
                                      </p>

                                      {!isCorrect && !isProbabilistic(question) && (
                                        <p className="font-semibold break-words leading-snug text-left">
                                          <span className="text-white/75">Correct Answer:</span>{' '}
                                          <MathRenderer content={wrapBareLatex(correctAnswer)} />
                                        </p>
                                      )}
                                    </>
                                  )}
                                  {isProbabilistic(question) && probabilisticResultById[questionId] && (
                                    <div className="mt-3">
                                      <ProbabilisticResponseFeedback {...probabilisticResultById[questionId]} theme="dark" />
                                    </div>
                                  )}
                                </div>

                                {question?.has_image && (
                                  <div className="flex flex-wrap gap-2 sm:justify-end sm:self-start sm:justify-self-end">
                                    <button
                                      type="button"
                                      onClick={() => handleImageToggle(questionId)}
                                      className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-sm hover:bg-white/20 transition"
                                    >
                                      <span>{imagesVisibility[questionId] ? 'Hide image' : 'Show image'}</span>
                                      <span className={`transition-transform ${imagesVisibility[questionId] ? 'rotate-180' : ''}`}>▾</span>
                                    </button>
                                  </div>
                                )}
                              </div>

                              {explanation && (
                                <ExplanationDisclosure
                                  open={!!explanationsVisibility[questionId]}
                                  triggerLabel={
                                    isProbabilistic(question) ? 'Extra explanation' : 'View explanation'
                                  }
                                  title={isProbabilistic(question) ? 'Extra explanation' : 'Explanation'}
                                  onToggle={() => handleExplanation(questionId)}
                                >
                                  <MathRenderer content={explanation} />
                                  <ProbabilisticCriteriaList
                                    criteria={probabilisticResultById[questionId]?.criteria}
                                    theme="dark"
                                  />
                                </ExplanationDisclosure>
                              )}

                              {question?.has_image && imagesVisibility[questionId] && (
                                <div className="mt-3">
                                  <QuestionImageDisplay questionId={question.id} />
                                </div>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizComplete;